import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { TbMessageReport } from "react-icons/tb";
import { isPublicStatusBrowseContext } from '../../utils/publicStatusPage';
import './GlobalFeedbackButton.css';

export const OPEN_GLOBAL_FEEDBACK_EVENT = 'open-global-feedback';

const GlobalFeedbackButton = () => {
  const isStatusRoute = isPublicStatusBrowseContext();
  
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackScreenshot, setFeedbackScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectorContext, setConnectorContext] = useState(null);
  const [feedbackPrefix, setFeedbackPrefix] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const connector = e.detail?.connector;
      const prefix = e.detail?.prefix;
      setConnectorContext(connector || null);
      setFeedbackPrefix(prefix || null);
      setFeedbackText('');
      setFeedbackScreenshot(null);
      setScreenshotPreview(null);
      setSubmitSuccess(false);
      setIsOpen(true);
    };
    window.addEventListener(OPEN_GLOBAL_FEEDBACK_EVENT, handler);
    return () => window.removeEventListener(OPEN_GLOBAL_FEEDBACK_EVENT, handler);
  }, []);
  
  // Hide feedback button on status page
  if (isStatusRoute) {
    return null;
  }

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Reset form when opening
      setFeedbackText('');
      setFeedbackScreenshot(null);
      setScreenshotPreview(null);
      setSubmitSuccess(false);
        setConnectorContext(null);
        setFeedbackPrefix(null);
    }
  };

  const handleScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFeedbackScreenshot(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (event) => {
        setScreenshotPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting || (!feedbackText.trim() && !feedbackScreenshot)) {
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      const prefix = feedbackPrefix || (connectorContext ? `Feedback about ${connectorContext}` : null);
      const textToSend = prefix
        ? `${prefix}: ${feedbackText.trim()}`
        : feedbackText;
      formData.append('feedbackText', textToSend);
      formData.append('feedbackType', feedbackPrefix ? 'request_connector' : 'global');
      formData.append('currentUrl', window.location.href);
      formData.append('userAgent', navigator.userAgent);
      formData.append('timestamp', new Date().toISOString());
      if (connectorContext) {
        formData.append('connector', connectorContext);
      }
      
      if (feedbackScreenshot) {
        formData.append('screenshot', feedbackScreenshot);
        console.log('Added screenshot to form data:', feedbackScreenshot.name);
      }

      console.log('🚀 Sending global feedback request');
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/feedback/global`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      console.log('✅ Global feedback submitted successfully:', response.data);
      
      // Show success state
      setSubmitSuccess(true);
      
      // Reset form after a delay
      setTimeout(() => {
        setIsOpen(false);
        setFeedbackText('');
        setFeedbackScreenshot(null);
        setScreenshotPreview(null);
        setSubmitSuccess(false);
        setConnectorContext(null);
        setFeedbackPrefix(null);
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error submitting global feedback:', error);
      // You might want to show a user-friendly error message here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setFeedbackText('');
    setFeedbackScreenshot(null);
    setScreenshotPreview(null);
    setSubmitSuccess(false);
    setConnectorContext(null);
    setFeedbackPrefix(null);
  };

  const handleRemoveScreenshot = () => {
    setFeedbackScreenshot(null);
    setScreenshotPreview(null);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      {/* Hover zone in bottom-right corner - button only appears when hovering here */}
      <div className={`global-feedback-hover-zone ${isOpen ? 'global-feedback-zone-active' : ''}`}>
        <button
          className="global-feedback-button"
          onClick={handleToggle}
          title="Send Feedback"
          aria-label="Send Feedback"
        >
          <TbMessageReport />
        </button>
      </div>

      {/* Feedback Modal - rendered via portal to appear above other modals */}
      {isOpen && createPortal(
        <div className="global-feedback-modal" style={{ zIndex: 10003 }}>
          <div className="global-feedback-content">
            {submitSuccess ? (
              <div className="feedback-success">
                <div className="success-icon">✓</div>
                <h3>Thank you!</h3>
                <p>{feedbackPrefix === 'Request a new connector' ? 'Your request has been submitted successfully.' : 'Your feedback has been submitted successfully.'}</p>
              </div>
            ) : (
              <>
                <div className="feedback-header">
                  <h3>{feedbackPrefix === 'Request a new connector' ? 'Request Connector' : 'Send Feedback'}</h3>
                  <button
                    className="close-button"
                    onClick={handleCancel}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                <div className="feedback-body">
                  <div className="feedback-field">
                    {feedbackPrefix !== 'Request a new connector' && (
                      <label htmlFor="feedback-text">
                        What would you like to share?
                      </label>
                    )}
                    {(connectorContext || feedbackPrefix) ? (
                      <div className="feedback-with-prefix">
                        <span className="feedback-prefix">{feedbackPrefix || `Feedback about ${connectorContext}`}: </span>
                        <textarea
                          id="feedback-text"
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          placeholder={feedbackPrefix === 'Request a new connector' ? "What app or integration would you like us to add?" : "Tell us about your experience, report a bug, or suggest an improvement..."}
                          rows={4}
                          className="feedback-text-with-prefix"
                        />
                      </div>
                    ) : (
                      <textarea
                        id="feedback-text"
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="Tell us about your experience, report a bug, or suggest an improvement..."
                        rows={4}
                      />
                    )}
                  </div>

                  {feedbackPrefix !== 'Request a new connector' && (
                    <div className="feedback-field">
                      <label>Add screenshot (optional)</label>
                      <div className="screenshot-help-text">
                        <p>To capture this page: Use your device's screenshot tool (⌘+Shift+4 on Mac, ⊞+Shift+S on Windows) and upload the image.</p>
                      </div>
                      <div className="screenshot-options">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleScreenshotUpload}
                          className="hidden-file-input"
                          id="global-feedback-upload"
                        />
                        <label
                          htmlFor="global-feedback-upload"
                          className="upload-button"
                          title="Upload an image file"
                        >
                          {feedbackScreenshot ? 'Change Image' : 'Upload Image'}
                        </label>
                      </div>
                      
                      {feedbackScreenshot && screenshotPreview && (
                        <div className="screenshot-preview">
                          <div className="screenshot-image-preview">
                            <img 
                              src={screenshotPreview} 
                              alt="Screenshot preview" 
                              className="preview-image"
                            />
                            <button
                              onClick={handleRemoveScreenshot}
                              className="remove-screenshot"
                              title="Remove screenshot"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="feedback-actions">
                  <button
                    onClick={handleCancel}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || (feedbackPrefix === 'Request a new connector' ? !feedbackText.trim() : (!feedbackText.trim() && !feedbackScreenshot))}
                    className="submit-button"
                  >
                    {isSubmitting ? 'Sending...' : (feedbackPrefix === 'Request a new connector' ? 'Send request' : 'Send Feedback')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GlobalFeedbackButton;
